import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import type z from 'zod';

import { InputField } from '@nesso/ui/primitives/input-field';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';

import {
    ehrPlatformsInputSchema,
    MAX_EHR_NAME_LENGTH,
    type StepProps,
    useUpdateInsightsOptions,
    useUpdateOnboardingStepOptions,
} from '@/entities/practice-member';
import { CardFormSection } from '@/shared/ui/forms/card-form-section';
import { RadioButtonItem } from '@/shared/ui/forms/radio-button-item';

export function OnboardingEhrPlatformStep(props: StepProps) {
    const form = useForm<z.infer<typeof ehrPlatformsInputSchema>>({
        mode: 'onChange',
        resolver: zodResolver(ehrPlatformsInputSchema),
        defaultValues: {
            type: 'ehr',
            ...(props.stepData?.ehr?.selection && { selection: props.stepData.ehr.selection }),
            ...(props.stepData?.ehr?.selection === 'OTHER' && {
                customValue: props.stepData.ehr.customValue,
            }),
        },
    });
    const {
        formState: { isDirty, isSubmitting, errors },
    } = form;

    const selectedValue = useWatch({
        control: form.control,
        name: 'selection',
    });

    const mutation = useMutation(useUpdateInsightsOptions());
    const updateOnboardingStepMutation = useMutation(useUpdateOnboardingStepOptions());

    const onSubmit = async (data: z.infer<typeof ehrPlatformsInputSchema>) => {
        try {
            if (props.nextStep) {
                updateOnboardingStepMutation.mutate(props.nextStep);
            }
            if (isDirty) {
                await mutation.mutateAsync(data);
            }
            props.onNext();
        } catch {
            console.error('Failed to update EHR platform step');
        }
    };

    const handleManualSubmit = () => {
        form.handleSubmit(onSubmit)();
    };

    return (
        <FormProvider {...form}>
            <CardFormSection
                title="What EHR are you using?"
                description="We’ll help you transfer your notes from Nesso to your current EHR"
                errorMessage={errors.selection?.message}
                isFatalError={mutation.isError}
                progressValue={props.progress}
                disabledBackButton={isSubmitting}
                disabledNextButton={isSubmitting}
                onNext={handleManualSubmit}
                onBack={props.onBack}
            >
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Controller
                        control={form.control}
                        name="selection"
                        render={({ field }) => (
                            <RadioGroup
                                {...field}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                                className="flex flex-col gap-6 w-full sm:max-w-[25rem]"
                            >
                                <RadioButtonItem value="ENSORA_HEALTH" title="Ensora Health" />
                                <RadioButtonItem value="PRACTICE_BETTER" title="Practice Better" />
                                <RadioButtonItem value="SESSIONS_HEALTH" title="Sessions Health" />
                                <RadioButtonItem value="SIMPLE_PRACTICE" title="Simple Practice" />
                                <RadioButtonItem value="THERAPY_NOTES" title="Therapy Notes" />
                                <RadioButtonItem
                                    value="OTHER"
                                    title="Other"
                                    className={selectedValue === 'OTHER' ? 'hidden' : undefined}
                                />
                            </RadioGroup>
                        )}
                    />
                    {selectedValue === 'OTHER' && (
                        <Controller
                            shouldUnregister
                            control={form.control}
                            name="customValue"
                            defaultValue=""
                            render={({ field, fieldState }) => (
                                <InputField
                                    {...field}
                                    autoFocus
                                    id={field.name}
                                    label="Other"
                                    description="Enter your EHR"
                                    value={field.value ?? ''}
                                    disabled={isSubmitting}
                                    maxLength={MAX_EHR_NAME_LENGTH}
                                    fieldProps={{
                                        className:
                                            'mt-4 animate-in fade-in slide-in-from-top-1 sm:max-w-[25rem]',
                                    }}
                                    error={fieldState.error?.message ?? fieldState.invalid}
                                />
                            )}
                        />
                    )}
                </form>
            </CardFormSection>
        </FormProvider>
    );
}
